import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import SharedAccountNotice from "../SharedAccountNotice";
import ConnectionFlowMap from "./ConnectionFlowMap";
import SelectionPanel, { NavSection } from "../../common/configPanel/SelectionPanel";
import ActionSectionForm from "../../common/configPanel/ActionSectionForm";
import { useConfigEditor } from "../../common/configPanel/useConfigEditor";
import {
    actionCanCommitForm, actionNeedsSetup, hasFields, isAuth, isFlowAction, slugOf,
} from "../../common/configPanel/actionSetup";
import { actionLabel } from "../../common/configPanel/buildActionNav";
import { AccountBundle, FlowDirectionKey, Selection } from "./flowMapTypes";
import { buildFlowArrows, buildGraph, hasType, useAccountBundles } from "./useFlowGraph";
import { computeNeedsSetup } from "./needsSetup";
import { baseUrlPolicy } from "./baseUrlPolicy";
import { useTranslation } from "react-i18next";
import { getConnectionKind } from "../../common/connectionKind";
import { getEnvironment } from "../../common/getEnvironment";
import { CopyToClipboard, HideString, ShowString } from "../../common/ProjectIcons";
import { CodeSampleSwitcher } from "../codeSamples";
import ConnectionDetailsWebhook from "../ConnectionDetailsWebhook";
import { getDestinationAccount } from "../../destinations/DestinationHelperFunctions";
import { constructEarthRangerUrl, extractEarthRangerSiteName, isEarthRangerDestinationType } from "../../destinations/earthRangerUrl";
import EarthRangerSiteInput from "../../destinations/EarthRangerSiteInput";
import { getReverseConnections } from "../../../api/connections/connectionsApi";
import { buttonClasses } from "../../common/tailwindClassnames";
import ModalShell from "../../common/ModalShell";
import UnsavedSectionPrompt, { SectionPrompt } from "../../common/configPanel/UnsavedSectionPrompt";
// The portal's canonical switch (headless-ui + the shared switchContainer classes),
// reused here so the direction toggle looks like every other toggle in the app.
import CustomCheckbox from "../../common/SchemaFormTemplates/CustomCheckbox";
import { ActionPillButton } from "../flowIcons";
import { Rule } from "../transformationRules/fieldMappingTypes";

export function useSelectionFromUrl(connectionId: string): {
    selection: Selection;
    select: (next: Selection, options?: { replace?: boolean }) => void;
} {
    const navigate = useNavigate();
    // One splat route carries every selection depth:
    //   account/<integrationId>[/<section>]  |  flow/<destinationId>/<in|out>[/<section>]
    const splat = useParams()["*"] || "";
    const [kind, id, third, fourth] = splat.split("/").filter(Boolean);
    const base = `/connections/${connectionId}/configurations`;

    let selection: Selection = { kind: "none" };
    if (kind === "account" && id) {
        selection = { kind: "account", integrationId: id, section: third };
    } else if (kind === "flow" && id && (third === "in" || third === "out")) {
        selection = { kind: "flow", destinationId: id, direction: third as FlowDirectionKey, section: fourth };
    }

    const select = (next: Selection, options?: { replace?: boolean }) => {
        const opts = options?.replace ? { replace: true } : undefined;
        if (next.kind === "none") return navigate(base, opts);
        if (next.kind === "account")
            return navigate(`${base}/account/${next.integrationId}${next.section ? "/" + next.section : ""}`, opts);
        return navigate(`${base}/flow/${next.destinationId}/${next.direction}${next.section ? "/" + next.section : ""}`, opts);
    };

    return { selection, select };
}

const ConfigurationsTab = (props: any) => {
    const { connection, connectionProvider, token, currentMemberIsAdmin, apiKey } = props;
    const { t } = useTranslation();
    const navigate = useNavigate();
    const providerId: string = connectionProvider?.id || connection?.provider?.id;
    const destinations: any[] = React.useMemo(() => connection?.destinations || [], [connection?.destinations]);
    const accountIds = React.useMemo(
        () => [providerId, ...destinations.map((d: any) => d.id)].filter(Boolean),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [providerId, destinations.map((d: any) => d.id).join(",")]);

    // The parent fetches the provider integration itself — seed the bundle map
    // with it instead of re-fetching the same record.
    const { bundles, loading, failed, reload } = useAccountBundles(token, accountIds, providerId, connectionProvider);
    const { selection, select } = useSelectionFromUrl(connection?.id);

    const selectedAccountId = selection.kind === "account" ? selection.integrationId : undefined;
    const selectedArrowId = selection.kind === "flow" ? `${selection.destinationId}-${selection.direction}` : undefined;

    // Structural graph: every input is either a primitive or identity-stable
    // parent state, so a plain dependency array works. Selection is layered on
    // afterwards so clicking around never rebuilds nodes and edges.
    const structural = React.useMemo(() => {
        const built = buildFlowArrows({
            providerId,
            provider: connection?.provider,
            destinations,
            reverseRoutesBySite: props.reverseRoutesBySite || {},
            providerCanReceive: !!props.providerCanReceive,
            // undefined while the destination's integration hasn't loaded — the
            // arrow is drawn unavailable but claims no reason for it.
            destinationCanSend: (destId: string) => bundles[destId] ? hasType(bundles[destId].actions, "pull") : undefined,
            t,
        });

        // Who still has work to do — one shared answer for the node badges, the
        // arrow badges and the Quick Start steps.
        const setup = computeNeedsSetup({
            providerId, connectionProvider, bundles, destinations, arrows: built,
        });
        const { needsSetupAccounts, needsSetupArrows } = setup;

        const graph = buildGraph({
            provider: connection?.provider,
            destinations,
            arrows: built,
            bundles,
            destinationsWithRules: props.destinationsWithRules || new Set<string>(),
            needsSetupAccounts,
            needsSetupArrows,
            t,
        });
        return { ...graph, ...setup, arrows: built };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [providerId, connection?.provider, connectionProvider, destinations, bundles,
        props.reverseRoutesBySite, props.providerCanReceive, props.destinationsWithRules, t]);

    const arrows = structural.arrows;

    // The Quick Start banner lives on the page above this tab and has no view of
    // the per-account configurations, so report what is still unconfigured.
    const { onNeedsSetupChange } = props;
    const needsSetupCount = loading ? null : structural.needsSetupCount;
    const providerNeedsSetup = structural.providerNeedsSetup;
    const destinationsNeedSetup = structural.destinationsNeedSetup;
    // Nothing loaded at all (no token yet, or it expired) is not a report: with an
    // empty bundle map every account would read as unconfigured and the banner
    // would flip a finished connection to all-warnings.
    const anyBundleLoaded = Object.keys(bundles).length > 0;
    useEffect(() => {
        if (needsSetupCount !== null && anyBundleLoaded) onNeedsSetupChange?.({
            total: needsSetupCount, provider: providerNeedsSetup, destinations: destinationsNeedSetup,
        });
    }, [needsSetupCount, providerNeedsSetup, destinationsNeedSetup, anyBundleLoaded, onNeedsSetupChange]);

    // A map with a single account has nothing to choose between — select it so its
    // configuration is on screen instead of an empty panel asking for a click.
    // Replaces the history entry: back should leave the tab, not the auto-select.
    const soleAccountId = structural.nodes.length === 1 ? structural.nodes[0].id : undefined;
    useEffect(() => {
        if (selection.kind === "none" && soleAccountId)
            select({ kind: "account", integrationId: soleAccountId }, { replace: true });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selection.kind, soleAccountId]);

    const { nodes, edges } = React.useMemo(() => ({
        nodes: structural.nodes.map((n: any) =>
            n.id === selectedAccountId ? { ...n, data: { ...n.data, isSelected: true } } : n),
        edges: structural.edges.map((e: any) =>
            e.id === selectedArrowId ? { ...e, data: { ...e.data, isSelected: true } } : e),
    }), [structural, selectedAccountId, selectedArrowId]);

    // Confirmations render through the shared ModalShell rather than window.confirm,
    // so they match every other dialog in the portal.
    type Confirmation = {
        title: string; body: string; confirmLabel: string; destructive?: boolean; onConfirm: () => void;
    };
    const [confirmation, setConfirmation] = useState<Confirmation | null>(null);

    // Saving is per section: moving to another section while the current one has
    // unsaved edits asks whether to save that section or discard it, so edits
    // never ride along silently into a Save pressed somewhere else.
    const [sectionPrompt, setSectionPrompt] = useState<SectionPrompt | null>(null);

    // ---- unsaved edits, stashed per account and PATCHed on Save -------------
    // Shared with every other configuration screen (destination page, wizard) so
    // "Save" always means "PATCH this one section".
    const editor = useConfigEditor({ token, t, onSaved: (id: string) => reload(id) });
    const { anyDirty } = editor;

    // Switching to a different node/arrow throws away whatever is stashed for the
    // current one, so ask first. Switching sections inside the same selection keeps
    // the same pending stash and never prompts.
    const guardedSelect = (next: Selection) => {
        const sameAccount = next.kind === "account" && selection.kind === "account" && next.integrationId === selection.integrationId;
        const sameFlow = next.kind === "flow" && selection.kind === "flow"
            && next.destinationId === selection.destinationId && next.direction === selection.direction;
        // The provider's URL draft is unsaved state too, even though it is not an
        // action configuration.
        if ((anyDirty || baseUrlDraft !== null) && !sameAccount && !sameFlow) {
            // The same Save-or-discard question a section switch asks. Offering
            // only "discard" here made the user cancel, save by hand, and come
            // back to click where they wanted to go in the first place.
            return setSectionPrompt({
                onSave: savePendingEdits,
                onDiscard: discardPendingEdits,
                proceed: () => select(next),
            });
        }
        select(next);
    };

    // Leaving the connection entirely deserves the same prompt as moving between
    // selections — nothing else blocks the route, so unsaved edits would just
    // vanish when the panel unmounts.
    const guardedNavigate = (to: string) => {
        if (anyDirty || baseUrlDraft !== null) {
            return setSectionPrompt({
                onSave: savePendingEdits,
                onDiscard: discardPendingEdits,
                proceed: () => navigate(to),
            });
        }
        navigate(to);
    };

    // ---- other connections sharing a destination account -------------------
    // Fetched lazily for the destination whose panel is open (the notice only
    // renders there), and cached per id — never one call per destination upfront.
    const [sharedBySite, setSharedBySite] = useState<Record<string, { id: string, name: string }[]>>({});
    const sharedFetched = useRef<Set<string>>(new Set());
    const selectedDestId = selection.kind === "account" && selection.integrationId !== providerId
        ? selection.integrationId : undefined;
    useEffect(() => {
        if (!token || !selectedDestId || sharedFetched.current.has(selectedDestId)) return;
        sharedFetched.current.add(selectedDestId);
        let cancelled = false;
        (async () => {
            try {
                const data = await getReverseConnections(token, selectedDestId);
                const others = (data?.results || data || [])
                    .filter((c: any) => c?.id && c.id !== connection?.id)
                    // No name fallback to the raw id: a connection the user can't
                    // resolve renders as a masked count, never as an identifier.
                    .map((c: any) => ({ id: c.id, name: c.provider?.name }));
                if (!cancelled) setSharedBySite((prev) => ({ ...prev, [selectedDestId]: others }));
            } catch (e) {
                // Allow a retry on the next visit to this panel.
                sharedFetched.current.delete(selectedDestId);
                console.error("failed to load connections sharing site", selectedDestId, e);
            }
        })();
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [token, selectedDestId, connection?.id]);

    // ---- section content ---------------------------------------------------
    const renderActionForm = (bundle: AccountBundle, action: any, editable: boolean) => (
        <ActionSectionForm
            target={bundle}
            action={action}
            editable={editable}
            userIsAdmin={currentMemberIsAdmin}
            token={token}
            formVersion={editor.formVersion[bundle.id] || 0}
            errors={editor.formErrors[bundle.id]?.[action.id]}
            formData={editor.formDataFor(bundle, action)}
            onChange={(data: any, fieldId?: string) => editor.snapshotChange(bundle, action.id, data, fieldId)}
            test={props.onTestAction
                ? {
                    // Every node on the map is a different integration, so the test has
                    // to name the account it belongs to.
                    onTestAction: (id: any, value: any) => props.onTestAction(id, value, bundle),
                    loading: props.testActionLoading,
                    response: props.testActionResponse,
                }
                : undefined}
        />
    );

    // Field mapping lives on the route (per flow), not on the account, and is
    // edited in the existing transformation-rules side panel.
    const fieldMappingSection = (dest: any) => {
        const rule: Rule | null = dest ? props.getRule(dest.id) : null;
        // Nothing on this flow says what a rule is until one exists, so the empty
        // state explains it rather than offering a bare button.
        if (!rule) return (
            <>
                <p className="text-sm text-secondary-text text-center max-w-xl mx-auto mt-5">
                    {t("Rules change the data on its way to this destination")}
                </p>
                <ActionPillButton label={t("Add Rules")} onClick={() => props.onOpenRules(dest)} />
            </>
        );
        const mapEntries = rule.mapping.map ? Object.entries(rule.mapping.map) : [];
        return (
            <>
                <div className="flex items-center gap-3 mb-2">
                    <span className="text-sm font-semibold text-off-black">{t("Field mapping")}</span>
                    <button className="ml-auto text-connection-green text-sm font-semibold underline underline-offset-2"
                        onClick={() => props.onOpenRules(dest)}>
                        {t("Edit")} ▸
                    </button>
                </div>
                <div className="rounded border border-gray-lines bg-secondary-background px-4 py-3 text-sm">
                    <div className="flex items-center gap-2 mb-2">
                        <span className="bg-light-green text-connection-green rounded px-2 py-0.5 text-xs font-semibold">
                            {rule.streamType === "ev" ? t("Events") : t("Observations")}
                        </span>
                        <span className="font-mono text-xs text-off-black">
                            {rule.mapping.provider_field || t("(source value)")} → {rule.mapping.destination_field}
                        </span>
                    </div>
                    {mapEntries.length > 0 && (
                        <div className="flex flex-col gap-1">
                            {mapEntries.slice(0, 4).map(([from, to]) => (
                                <div key={from} className="flex items-center gap-2 font-mono text-xs text-secondary-text">
                                    <span className="bg-white border border-gray-lines rounded px-2 py-0.5">{from}</span>
                                    <span className="text-connection-green font-bold">→</span>
                                    <span className="bg-white border border-gray-lines rounded px-2 py-0.5">{String(to)}</span>
                                </div>
                            ))}
                            {mapEntries.length > 4 && (
                                <span className="text-xs text-secondary-text">{t("+ {{count}} more…", { count: mapEntries.length - 4 })}</span>
                            )}
                        </div>
                    )}
                    {rule.mapping.default !== undefined && (
                        <div className="font-mono text-xs text-secondary-text mt-1">{t("default")}: {String(rule.mapping.default)}</div>
                    )}
                </div>
            </>
        );
    };

    // ---- how to connect (provider API key + endpoint) -----------------------
    // Push/webhook providers are configured by POSTing to Gundi, so their panel
    // leads with the endpoint + API key that used to live on the Provider tab.
    const [keyVisible, setKeyVisible] = useState(false);
    // Draft of the selected account's base URL; null means "not edited". Every
    // account has an endpoint now, so the draft has to be dropped when the
    // selection moves — guardedSelect can't catch a selection driven by the URL
    // (browser back/forward, a pasted deep link), and a draft left behind would be
    // saved onto whichever account is open next.
    const [baseUrlDraft, setBaseUrlDraft] = useState<string | null>(null);
    const [savingBaseUrl, setSavingBaseUrl] = useState(false);
    useEffect(() => { setBaseUrlDraft(null); }, [selectedAccountId]);
    // Same rules the destination form applies. EarthRanger accounts edit a site
    // name rather than a URL, so they validate as a hostname label and the full
    // URL is constructed on save.
    const baseUrlErrorFor = (draft: string | null, asSiteName: boolean, optional = false) => {
        if (draft === null) return undefined;
        const value = draft.trim();
        if (value.length === 0) {
            if (optional) return undefined;
            return asSiteName ? t("Site name is required.") : t("URL is required.");
        }
        if (asSiteName) {
            if (!/^[a-zA-Z0-9.-]+$/.test(value)) return t("Enter a valid site name.");
            try { new URL(constructEarthRangerUrl(value)); return undefined; } catch { return t("Enter a valid site name."); }
        }
        try { new URL(value); return undefined; } catch { return t("Enter a valid URL including the scheme."); }
    };
    // Everything the Endpoint section needs to know about an account's address.
    // Lifted out of the panel so the navigation guards can save that draft too —
    // they fire from outside the panel, but the draft is just as unsaved as a
    // stashed form section.
    const baseUrlStateFor = (bundle: AccountBundle) => {
        const policy = baseUrlPolicy({
            isProvider: bundle.isProvider,
            techName: bundle.techName,
            techValue: bundle.techValue || connectionProvider?.type?.value,
            actions: bundle.actions,
        });
        // EarthRanger sites are addressed by name (<site>.pamdas.org). A site whose
        // stored URL isn't a pamdas one is edited as a plain URL — rewriting it
        // through the site input would silently move it to a different host.
        const erSiteName = isEarthRangerDestinationType(bundle.techName)
            ? extractEarthRangerSiteName(bundle.base_url || "") : "";
        const asSiteName = isEarthRangerDestinationType(bundle.techName)
            && (!bundle.base_url || !!erSiteName);
        return {
            ...policy,
            asSiteName,
            value: asSiteName ? erSiteName : (bundle.base_url ?? ""),
        };
    };
    const selectedBundle = selectedAccountId ? bundles[selectedAccountId] : undefined;
    const selectedBaseUrl = selectedBundle ? baseUrlStateFor(selectedBundle) : undefined;
    const baseUrlError = baseUrlErrorFor(
        baseUrlDraft, !!selectedBaseUrl?.asSiteName, !!selectedBaseUrl?.optional);

    // Resolves true when there is nothing left unsaved about the address —
    // including when there was never a draft to begin with.
    const saveBaseUrl = async (): Promise<boolean> => {
        if (baseUrlDraft === null || !selectedBundle || !selectedBaseUrl) return true;
        if (baseUrlError) return false;
        const typed = baseUrlDraft.trim();
        if (typed === selectedBaseUrl.value) { setBaseUrlDraft(null); return true; }
        setSavingBaseUrl(true);
        try {
            // Keep the draft when the save didn't land, so the user's typing isn't
            // silently replaced by the stored value.
            const ok = await props.onSaveAccountBaseUrl?.(
                selectedBundle.id, selectedBaseUrl.asSiteName ? constructEarthRangerUrl(typed) : typed);
            if (ok === false) return false;
            setBaseUrlDraft(null);
            reload(selectedBundle.id);
            return true;
        } finally { setSavingBaseUrl(false); }
    };

    // Everything the user has stashed anywhere in this tab. The navigation guards
    // are leaving the whole selection behind, so they commit all of it rather than
    // just the section on screen.
    const savePendingEdits = async (): Promise<boolean> => {
        if (!(await saveBaseUrl())) return false;
        const targets = Object.keys(editor.dirtyIds)
            .filter((id) => editor.dirtyIds[id])
            .map((id) => bundles[id])
            .filter(Boolean);
        for (const target of targets) {
            if (!(await editor.save(target))) return false;
        }
        return true;
    };
    const discardPendingEdits = () => { editor.discardAll(); setBaseUrlDraft(null); };

    const copyText = (text: string, label: string) =>
        navigator.clipboard.writeText(text)
            .then(() => toast.info(t("{{label}} copied to clipboard", { label })))
            .catch(() => toast.error(t("Failed to copy to clipboard")));

    const howToConnectSection = (kind: string) => {
        const rawEnv = getEnvironment();
        const env = rawEnv === "local" ? "dev" : rawEnv;
        const endpoint = kind === "webhook"
            ? (env === "production" ? "https://hooks.gundiservice.org/webhooks" : `https://hooks.${env}.gundiservice.org/webhooks`)
            : (env === "production" ? "https://sensors.api.gundiservice.org/v2/observations/" : `https://sensors.api.${env}.gundiservice.org/v2/observations/`);
        const integrationType = connectionProvider?.type?.value || "";
        return (
            <div className="flex flex-col gap-4">
                <div className="rounded border border-field-outline bg-white p-4">
                    <p className="text-sm text-off-black mb-4">
                        {kind === "webhook"
                            ? t("Configure your source system to POST to the Gundi webhook endpoint below. Include your API key and the integration-type header with every request.")
                            : t("Send data to the Gundi observations endpoint below, including your API key with every request.")}
                    </p>
                    <div className="rounded border border-field-outline bg-very-light-gray overflow-hidden">
                        <div className="flex items-center justify-between gap-4 px-4 py-3 border-b border-divider-lines">
                            <div className="flex flex-col min-w-0">
                                <span className="text-xs text-secondary-gray mb-0.5">{t("Endpoint")}</span>
                                <span className="font-mono text-sm text-off-black truncate">{endpoint}</span>
                            </div>
                            <button type="button" onClick={() => copyText(endpoint, t("Endpoint"))}
                                className="text-secondary-gray hover:text-off-black flex-shrink-0" aria-label={t("Copy endpoint")}>
                                <CopyToClipboard />
                            </button>
                        </div>
                        {kind === "webhook" && (
                            <div className="grid grid-cols-2 border-b border-divider-lines bg-very-light-gray px-4 py-2">
                                <span className="text-xs text-secondary-gray">{t("Header")}</span>
                                <span className="text-xs text-secondary-gray">{t("Value")}</span>
                            </div>
                        )}
                        <div className={`${kind === "webhook" ? "grid grid-cols-2" : "flex items-center justify-between gap-4"} border-b border-divider-lines`}>
                            {kind === "webhook" ? (
                                <div className="flex items-center justify-between gap-2 px-4 py-3 border-r border-divider-lines">
                                    <span className="font-mono text-sm text-off-black">apikey</span>
                                    <button type="button" onClick={() => copyText("apikey", t("Header name"))}
                                        className="text-secondary-gray hover:text-off-black flex-shrink-0" aria-label={t("Copy header name")}>
                                        <CopyToClipboard />
                                    </button>
                                </div>
                            ) : (
                                <div className="flex flex-col min-w-0 px-4 py-3">
                                    <span className="text-xs text-secondary-gray mb-0.5">{t("API key")}</span>
                                    <span className="font-mono text-sm text-off-black truncate">
                                        {apiKey ? (keyVisible && currentMemberIsAdmin ? apiKey : "••••••••••••••••") : t("Loading…")}
                                    </span>
                                </div>
                            )}
                            <div className="flex items-center justify-between gap-2 px-4 py-3">
                                {kind === "webhook" && (
                                    <span className="font-mono text-sm text-off-black flex-1 truncate">
                                        {apiKey ? (keyVisible && currentMemberIsAdmin ? apiKey : "••••••••••••••••") : t("Loading…")}
                                    </span>
                                )}
                                {/* Revealing and copying the key stays an admin action, as it
                                    was on the provider tab this panel replaced. */}
                                {currentMemberIsAdmin && (
                                    <span className="flex items-center gap-2 flex-shrink-0 ml-auto">
                                        <button type="button" onClick={() => setKeyVisible((v) => !v)}
                                            className="text-secondary-gray hover:text-off-black" aria-label={keyVisible ? t("Hide API key") : t("Show API key")}>
                                            {keyVisible ? <HideString /> : <ShowString />}
                                        </button>
                                        <button type="button" onClick={() => apiKey && copyText(apiKey, t("API key"))}
                                            className="text-secondary-gray hover:text-off-black" aria-label={t("Copy API key")}>
                                            <CopyToClipboard />
                                        </button>
                                    </span>
                                )}
                            </div>
                        </div>
                        {kind === "webhook" && (
                            <div className="grid grid-cols-2">
                                <div className="flex items-center justify-between gap-2 px-4 py-3 border-r border-divider-lines">
                                    <span className="font-mono text-sm text-off-black">x-gundi-integration-type</span>
                                    <button type="button" onClick={() => copyText("x-gundi-integration-type", t("Header name"))}
                                        className="text-secondary-gray hover:text-off-black flex-shrink-0" aria-label={t("Copy header name")}>
                                        <CopyToClipboard />
                                    </button>
                                </div>
                                <div className="flex items-center justify-between gap-2 px-4 py-3">
                                    <span className="font-mono text-sm text-off-black truncate">{integrationType}</span>
                                    <button type="button" onClick={() => copyText(integrationType, t("Header value"))}
                                        className="text-secondary-gray hover:text-off-black flex-shrink-0" aria-label={t("Copy header value")}>
                                        <CopyToClipboard />
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
                <div className="rounded border border-field-outline bg-white p-4">
                    <p className="text-sm text-off-black mb-4">
                        {kind === "webhook"
                            ? t("Share this sample code with any developers integrating with this connection. Every request must include the apikey and x-gundi-integration-type headers, a timestamp preferably in ISO 8601 format, latitude and longitude, and a device or source ID.")
                            : t("Share this sample code and API key with any developers who need to push data to this connection. They can use it as a starting point to integrate their system directly.")}
                    </p>
                    <CodeSampleSwitcher
                        kind={kind === "webhook" ? "webhook" : "api-push"}
                        endpoint={endpoint}
                        apiKey={keyVisible && currentMemberIsAdmin ? apiKey : null}
                        integrationType={integrationType}
                        height={kind === "webhook" ? "280px" : "240px"}
                    />
                    <p className="text-sm text-off-black mt-4">
                        <a href={kind === "webhook"
                            ? "https://support.earthranger.com/developer_docs/custom-webhook"
                            : "https://support.earthranger.com/developer_docs/gundi-api"}
                            target="_blank" rel="noreferrer" className="text-blue-basic underline">
                            {kind === "webhook" ? t("Learn more about Gundi's webhook API.") : t("Learn more about Gundi's API.")}
                        </a>
                    </p>
                </div>
            </div>
        );
    };

    const accountPanel = () => {
        if (selection.kind !== "account") return null;
        const bundle = bundles[selection.integrationId];
        if (!bundle) {
            return (
                <div className="border border-gray-lines rounded-lg bg-white p-5">
                    {[0, 1, 2].map((i) => <div key={i} className="h-4 w-48 bg-neutral-gray-50 rounded animate-pulse mb-3" />)}
                </div>
            );
        }
        const dest = destinations.find((d: any) => d.id === bundle.id);

        // Pull/push configuration belongs to the arrows, so an account only shows
        // its credentials and its other (non-directional) actions.
        const generalActions = bundle.actions.filter((a) => !isFlowAction(a) && hasFields(a));
        const ordered = [...generalActions].sort((a, b) => Number(isAuth(b)) - Number(isAuth(a)));
        const nav: NavSection[] = [];
        if (ordered.length > 0) {
            nav.push({
                cat: t("GENERAL"),
                items: ordered.map((a) => ({
                    slug: slugOf(a),
                    label: actionLabel(a, t),
                    needsSetup: actionNeedsSetup(bundle, a),
                })),
            });
        }
        if (!bundle.isProvider) nav.push({ cat: t("RULES"), items: [{ slug: "field-mapping", label: t("Field mapping") }] });
        const providerType = bundle.isProvider ? connectionProvider?.type : undefined;
        // Webhook providers keep the webhook endpoint + headers; everything else
        // gets the sensors endpoint. A type that carries a webhook is a webhook
        // here even when it also has pull config (kind would say "standard").
        const connectionKind = providerType && (getConnectionKind(providerType) === "webhook" || providerType.webhook)
            ? "webhook" : "api-push";
        // Webhook payload parsing is provider-side config, so it lives on this
        // panel (it used to be its own tab).
        const webhookConfig = bundle.isProvider && (connectionProvider?.webhook_configuration || connectionProvider?.type?.webhook)
            ? (connectionProvider.webhook_configuration ?? {
                id: "", integration: connectionProvider.id,
                webhook: connectionProvider.type?.webhook ?? { id: "", name: "", value: "" },
                data: null,
            })
            : undefined;
        if (webhookConfig) {
            nav.push({
                cat: t("WEBHOOK"),
                items: [{ slug: "webhook", label: t("Payload parsing"), needsSetup: !connectionProvider?.webhook_configuration?.data }],
            });
        }
        // Last section on the provider, and always present: whatever the
        // integration method, the connection has an API key its developers may need.
        if (providerType) {
            nav.push({ cat: t("CONNECT"), items: [{ slug: "how-to-connect", label: t("API key") }] });
        }

        // base_url lives on the integration record, not in an action configuration,
        // so it gets a section of its own rather than a form section. The state
        // itself is lifted, so the navigation guards can save this draft too.
        const { show: showBaseUrl, optional: urlIsOptional, derived: urlIsDerived,
            asSiteName, value: baseUrlValue } = selectedBaseUrl ?? baseUrlStateFor(bundle);
        if (showBaseUrl) {
            const endpointItem = { slug: "endpoint", label: t("Endpoint"), needsSetup: !bundle.base_url && !urlIsOptional };
            // base_url lives on the integration record rather than in an action
            // configuration, but to the user it is one more thing to set on this
            // account — so it joins the GENERAL list instead of standing alone. It
            // heads that list when Gundi can't reach the account without it, and
            // trails it when the address is derived from configuration: the first
            // item is where the panel opens, and that should be the work that
            // actually has to be done.
            const general = nav.find((section) => section.cat === t("GENERAL"));
            if (general) general.items = urlIsOptional
                ? [...general.items, endpointItem]
                : [endpointItem, ...general.items];
            else nav.unshift({ cat: t("GENERAL"), items: [endpointItem] });
        }

        // A section the URL names but this account doesn't offer (an "endpoint" deep
        // link for a technology with a fixed address, say) would render an empty
        // panel with no way back — fall back to the account's first section.
        const navSlugs = nav.flatMap((section) => section.items.map((item) => item.slug));
        const urlSlug = selection.section && navSlugs.includes(selection.section) ? selection.section : undefined;
        const activeSlug = urlSlug || nav[0]?.items[0]?.slug || (bundle.isProvider ? undefined : "field-mapping");
        const activeAction = ordered.find((a) => slugOf(a) === activeSlug);
        const healthy = String(bundle.status || "").toLowerCase() === "healthy";
        // Per section, like Save itself: another section of the same account may
        // still be stashed, but this footer does not speak for it.
        const isDirty = !!activeAction && editor.isActionDirty(bundle.id, activeAction.id);
        // An action that was never saved, or whose saved data is still missing
        // required fields, can be committed as-is — so Save isn't dead until the
        // user pokes a field.
        const canCommitDefaults = !!activeAction && actionCanCommitForm(bundle, activeAction);
        // EarthRanger only: the ER account (username or truncated token) this
        // destination authenticates with — the bundle already has the configs.
        const erAccount = dest ? getDestinationAccount({ ...dest, configurations: bundle.configurations } as any) : null;

        const goToSection = (slug: string) => select({ kind: "account", integrationId: bundle.id, section: slug });
        const selectSection = (slug: string) => {
            if (slug === activeSlug) return;
            if (activeSlug === "endpoint" && baseUrlDraft !== null) {
                return setSectionPrompt({
                    onSave: saveBaseUrl,
                    onDiscard: () => setBaseUrlDraft(null),
                    proceed: () => goToSection(slug),
                });
            }
            if (activeAction && editor.isActionDirty(bundle.id, activeAction.id)) {
                return setSectionPrompt({
                    onSave: () => editor.save(bundle, activeAction.id),
                    onDiscard: () => editor.cancel(bundle, activeAction.id),
                    proceed: () => goToSection(slug),
                });
            }
            goToSection(slug);
        };

        return (
            <SelectionPanel
                title={bundle.name}
                subtitle={<span className="flex items-center gap-2">
                    {bundle.base_url && <span>· {bundle.base_url}</span>}
                    {erAccount && <span className="font-mono">· {erAccount}</span>}
                    <span className={`w-2 h-2 rounded-full ${healthy ? "bg-connection-green" : "bg-medium-gray"}`} title={bundle.status || t("unknown")} />
                </span>}
                headerActions={<>
                    {/* The account's own screen: every connection using it, and the
                        record itself outside this connection's context. Routed through
                        the same discard prompt as a selection change. */}
                    <button type="button"
                        onClick={() => guardedNavigate(`/connected-systems/${bundle.id}/configuration`)}
                        className="text-connection-green text-sm font-semibold underline underline-offset-2 self-center">
                        {t("View connected system")} ▸
                    </button>
                    {!bundle.isProvider && dest && (
                        <button className={buttonClasses.lightRed}
                            onClick={() => setConfirmation({
                                title: t("Remove from connection"),
                                body: t("Remove {{name}} from this connection? The site itself is not deleted — it just stops receiving data from this flow.", { name: bundle.name }),
                                confirmLabel: t("Remove"),
                                destructive: true,
                                onConfirm: () => { Promise.resolve(props.onRemoveSite(dest)).then(() => select({ kind: "none" })); },
                            })}>
                            {t("Remove from connection")}
                        </button>
                    )}
                </>}
                // The shared-account warning is about config stored on the account.
                // Field mappings live on this connection's route, so the notice
                // would be plainly wrong in that section.
                banner={activeSlug === "field-mapping" ? undefined : <SharedAccountNotice
                    accountName={bundle.name}
                    usedBy={bundle.isProvider ? (props.providerSharedWith || []) : (sharedBySite[bundle.id] || [])} />}
                nav={nav}
                activeSlug={activeSlug}
                onSelectSection={selectSection}
                emptyNavNote={t("No general configuration — pull and push settings live on the arrows.")}
                footer={activeSlug === "endpoint" && showBaseUrl ? <>
                    <button className={buttonClasses.primary}
                        disabled={baseUrlDraft === null || !!baseUrlError
                            || baseUrlDraft.trim() === baseUrlValue || savingBaseUrl}
                        onClick={saveBaseUrl}>{t("Save")}</button>
                    <button className={buttonClasses.cancel} disabled={baseUrlDraft === null}
                        onClick={() => setBaseUrlDraft(null)}>{t("Cancel")}</button>
                </> : activeAction ? <>
                    <button className={buttonClasses.primary} disabled={!isDirty && !canCommitDefaults}
                        onClick={() => { editor.commitDefaults(bundle, activeAction); editor.save(bundle, activeAction.id); }}>{t("Save")}</button>
                    <button className={buttonClasses.cancel} disabled={!isDirty}
                        onClick={() => editor.cancel(bundle, activeAction.id)}>{t("Cancel")}</button>
                </> : undefined}>
                {activeSlug === "endpoint" && showBaseUrl
                    ? <div className="rounded border border-field-outline bg-white p-4">
                        <div className="flex flex-col md:flex-row md:items-start md:justify-between w-full gap-2 md:gap-6">
                            <div className="md:w-1/2 flex flex-col">
                                <label className="mr-1">{asSiteName ? t("Site URL") : t("URL")}{urlIsOptional ? "" : " *"}</label>
                                <span className="text-sm text-secondary-gray mt-1">
                                    {asSiteName
                                        ? t("The name of the EarthRanger site (e.g. my-site).")
                                        : urlIsDerived
                                            ? t("This technology derives its address from its configuration. Set a URL only if you need to override it.")
                                            : t("The address Gundi uses to reach this system.")}
                                </span>
                            </div>
                            <div className="md:w-1/2">
                                {asSiteName
                                    ? <EarthRangerSiteInput
                                        value={baseUrlDraft ?? baseUrlValue}
                                        onChange={(e) => setBaseUrlDraft(e.target.value)}
                                        error={baseUrlError}
                                      />
                                    : <>
                                        <input
                                            id="provider_base_url"
                                            value={baseUrlDraft ?? baseUrlValue}
                                            onChange={(e) => setBaseUrlDraft(e.target.value)}
                                            className={`w-full p-1.5 border rounded px-2 transition-colors duration-300 ${
                                                baseUrlError ? "border-orange" : "border-neutral-gray-200"}`}
                                        />
                                        {baseUrlError && <p className="text-orange-600 text-sm mt-1">{baseUrlError}</p>}
                                      </>}
                            </div>
                        </div>
                      </div>
                    : activeSlug === "how-to-connect"
                    ? howToConnectSection(connectionKind)
                    : activeSlug === "webhook" && webhookConfig
                        ? <ConnectionDetailsWebhook
                            webhookConfiguration={webhookConfig}
                            currentMemberIsAdmin={currentMemberIsAdmin}
                            onSave={props.onSaveWebhook}
                            onCancel={props.onCancelWebhook}
                            changesMade={props.webhookChangesMade}
                            onChangesMade={props.onWebhookChangesMade}
                          />
                        : activeAction
                            ? renderActionForm(bundle, activeAction, true)
                            : activeSlug === "field-mapping"
                                ? fieldMappingSection(dest)
                                : <p className="text-secondary-text text-sm">{t("This account has no general configuration.")}</p>}
            </SelectionPanel>
        );
    };

    const flowPanel = () => {
        if (selection.kind !== "flow") return null;
        const arrow = arrows.find((a) => a.id === `${selection.destinationId}-${selection.direction}`);
        const providerBundle = bundles[providerId];
        const destBundle = bundles[selection.destinationId];
        const dest = destinations.find((d: any) => d.id === selection.destinationId);
        if (!arrow || !providerBundle || !destBundle) {
            return (
                <div className="border border-gray-lines rounded-lg bg-white p-5">
                    {[0, 1, 2].map((i) => <div key={i} className="h-4 w-48 bg-neutral-gray-50 rounded animate-pulse mb-3" />)}
                </div>
            );
        }

        const sender = arrow.direction === "out" ? providerBundle : destBundle;
        const receiver = arrow.direction === "out" ? destBundle : providerBundle;
        const senderPulls = sender.actions.filter((a: any) => a.type === "pull" && hasFields(a));
        const receiverPushes = receiver.actions.filter((a: any) => a.type === "push" && hasFields(a));
        const nav: NavSection[] = [];
        // The nav headers name the technology, not the account: "FROM EARTHRANGER"
        // reads as the kind of system data comes from, and account names are
        // already in the panel title.
        const techOf = (b: AccountBundle) => b.techName || b.name;
        if (senderPulls.length) nav.push({ cat: t("FROM {{name}}", { name: techOf(sender) }).toUpperCase(), items: senderPulls.map((a: any) => ({ slug: `from-${slugOf(a)}`, label: actionLabel(a, t), needsSetup: actionNeedsSetup(sender, a) })) });
        if (receiverPushes.length) nav.push({ cat: t("TO {{name}}", { name: techOf(receiver) }).toUpperCase(), items: receiverPushes.map((a: any) => ({ slug: `to-${slugOf(a)}`, label: actionLabel(a, t), needsSetup: actionNeedsSetup(receiver, a) })) });

        const activeSlug = selection.section || nav[0]?.items[0]?.slug;
        const fromSender = !!activeSlug?.startsWith("from-");
        const owner = fromSender ? sender : receiver;
        const activeAction = (fromSender ? senderPulls : receiverPushes)
            .find((a: any) => `${fromSender ? "from" : "to"}-${slugOf(a)}` === activeSlug);
        const editable = arrow.state !== "not-enableable";
        // Same as the account panel: an action that was never saved, or that is
        // still missing required fields, can be committed with the values on
        // screen, so Save is live even before the user edits anything.
        const canCommitDefaults = !!activeAction && editable && actionCanCommitForm(owner, activeAction);
        // Saving is per section, so Save/Cancel act on the active section's owner
        // only — the section-switch prompt guarantees no other section is dirty.
        const isDirty = !!activeAction && editor.isActionDirty(owner.id, activeAction.id);

        const goToSection = (slug: string) =>
            select({ kind: "flow", destinationId: selection.destinationId, direction: selection.direction, section: slug });
        const selectSection = (slug: string) => {
            if (slug === activeSlug) return;
            if (activeAction && editor.isActionDirty(owner.id, activeAction.id)) {
                return setSectionPrompt({
                    onSave: () => editor.save(owner, activeAction.id),
                    onDiscard: () => editor.cancel(owner, activeAction.id),
                    proceed: () => goToSection(slug),
                });
            }
            goToSection(slug);
        };

        const headerActions = arrow.state === "enabled" && arrow.direction === "in" ? (
            <span className="flex items-center gap-2">
                <span className="text-xs text-secondary-text">{t("Sending")}</span>
                <CustomCheckbox label={t("Sending")} formData={true} onChange={() => dest && setConfirmation({
                    title: t("Stop sending data"),
                    body: t("Stop sending data from {{from}} to {{to}}? This removes the reverse route; the configuration itself is kept.", { from: destBundle.name, to: providerBundle.name }),
                    confirmLabel: t("Stop sending"),
                    destructive: true,
                    onConfirm: () => { Promise.resolve(props.onDisableReverseFlow(dest)).then(() => props.onReloadConnection()); },
                })} />
            </span>
        ) : arrow.state === "disabled" ? (
            <span className="flex items-center gap-2">
                <span className="text-xs text-secondary-text">{t("Enable")}</span>
                <CustomCheckbox label={t("Enable")} formData={false} onChange={() => {
                    if (dest) Promise.resolve(props.onEnableReverseFlow(dest)).then(() => props.onReloadConnection());
                }} />
            </span>
        ) : undefined;

        const note = arrow.state === "disabled"
            ? t("Not enabled yet. Configure the sections below, then turn this direction on.")
            : undefined;

        return (
            <SelectionPanel
                title={`${sender.name} → ${receiver.name}`}
                subtitle={note ? <span className="text-xs text-secondary-text">{note}</span> : undefined}
                headerActions={headerActions}
                banner={arrow.state === "not-enableable" && arrow.reason
                    // Same amber treatment as SharedAccountNotice — it's a warning, not a neutral note.
                    ? <div className="mb-5 rounded border border-orange-200 bg-orange-50 px-4 py-3 text-sm text-off-black">{arrow.reason}</div>
                    : undefined}
                nav={nav}
                activeSlug={activeSlug}
                onSelectSection={selectSection}
                emptyNavNote={t("Nothing to configure for this direction.")}
                footer={activeAction && editable ? <>
                    <button className={buttonClasses.primary} disabled={!isDirty && !canCommitDefaults}
                        onClick={() => { editor.commitDefaults(owner, activeAction); editor.save(owner, activeAction.id); }}>{t("Save")}</button>
                    <button className={buttonClasses.cancel} disabled={!isDirty}
                        onClick={() => editor.cancel(owner, activeAction.id)}>{t("Cancel")}</button>
                </> : undefined}>
                {activeAction
                    ? <div className={editable ? "" : "opacity-70 pointer-events-none"}>{renderActionForm(owner, activeAction, editable)}</div>
                    : <p className="text-secondary-text text-sm">
                        {nav.length === 0
                            ? t("This flow doesn't need any configuration — data moves on its own.")
                            : t("Select a section to configure.")}
                      </p>}
            </SelectionPanel>
        );
    };

    return (
        <div className="mt-8 px-4 pb-28">
            {failed && (
                <div className="mb-4 rounded border border-orange-200 bg-orange-50 px-4 py-3 text-sm text-off-black flex flex-wrap items-center gap-3">
                    <span>{t("Some accounts in this connection couldn't be loaded, so parts of the map may be incomplete.")}</span>
                    <button type="button" className={`${buttonClasses.lightGreen} ml-auto`} onClick={() => reload()}>
                        {t("Retry")}
                    </button>
                </div>
            )}
            <ConnectionFlowMap
                nodes={nodes}
                edges={edges}
                destinationCount={destinations.length}
                connectionStatus={connection?.status}
                connectionEnabled={connectionProvider?.enabled !== false}
                onSelectAccount={(id: string) => guardedSelect({ kind: "account", integrationId: id })}
                onSelectFlow={(destinationId: string, direction: FlowDirectionKey) => guardedSelect({ kind: "flow", destinationId, direction })}
                onAddDestination={props.onAddDestination}
                highlightAddDestination={!!props.highlightAddDestination}
            />
            {destinations.length === 0 && (
                <p className="text-secondary-text text-sm mb-6">{t("No destinations yet. Use the “Add destination” button to connect one.")}</p>
            )}
            {selection.kind === "none" && (
                <p className="text-secondary-text text-sm">
                    {loading ? t("Loading accounts…") : t("Select an account or a flow above to configure it.")}
                </p>
            )}
            {confirmation && (
                <ModalShell
                    title={confirmation.title}
                    onClose={() => setConfirmation(null)}
                    footer={<>
                        <button type="button"
                            className={confirmation.destructive ? buttonClasses.destructive : buttonClasses.primary}
                            onClick={() => { const run = confirmation.onConfirm; setConfirmation(null); run(); }}>
                            {confirmation.confirmLabel}
                        </button>
                        <button type="button" className={buttonClasses.cancel} onClick={() => setConfirmation(null)}>
                            {t("Cancel")}
                        </button>
                    </>}>
                    <p className="text-base text-off-black">{confirmation.body}</p>
                </ModalShell>
            )}
            <UnsavedSectionPrompt prompt={sectionPrompt} onClose={() => setSectionPrompt(null)} />
            {selection.kind === "account" && accountPanel()}
            {selection.kind === "flow" && flowPanel()}
        </div>
    );
};

export default ConfigurationsTab;
